/**
 * Scroll container abstraction for Capacitor iOS.
 *
 * On Capacitor iOS the scroll container is `.app-root` (height: 100dvh; overflow-y: auto),
 * so `window.scrollY` is always 0 and `window` scroll events never fire.
 * This utility returns the correct element / value for both web and native.
 */

import { isCapacitorIOS } from './capacitorPlatform';

let cachedRoot: HTMLElement | null = null;

function appRoot(): HTMLElement | null {
  if (!cachedRoot || !cachedRoot.isConnected) {
    cachedRoot = document.querySelector('.app-root') as HTMLElement | null;
  }
  return cachedRoot;
}

/** The element that fires scroll events and owns scrollTop. */
export function getScrollContainer(): HTMLElement | Window {
  if (isCapacitorIOS()) {
    return appRoot() || window;
  }
  return window;
}

/** Current vertical scroll position. */
export function getScrollY(): number {
  if (isCapacitorIOS()) {
    return appRoot()?.scrollTop ?? 0;
  }
  return window.scrollY;
}

/** Scroll the container to a given Y offset. */
export function scrollTo(top: number, behavior: ScrollBehavior = 'auto'): void {
  if (isCapacitorIOS()) {
    const el = appRoot();
    if (el) {
      el.scrollTo({ top, behavior });
      return;
    }
  }
  window.scrollTo({ top, behavior });
}

// ─── Diagnostics ────────────────────────────────────────────────────────

const SCROLL_DEBUG_KEY = 'scrollDebug';

/** Whether scroll diagnostics are enabled (set `?scrollDebug=1` in URL) */
export function isScrollDebugEnabled(): boolean {
  try {
    return new URLSearchParams(window.location.search).get(SCROLL_DEBUG_KEY) === '1';
  } catch {
    return false;
  }
}

interface CandidateSnapshot {
  selector: string;
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
  overflowY: string;
  isScrollable: boolean;
}

const CANDIDATE_SELECTORS = [
  '.app-root',
  '#root',
  'main',
  '[data-scroll-container]',
  '[data-radix-scroll-area-viewport]',
];

/** Snapshot all scroll candidate elements + any element with overflow-y scroll/auto that is actually scrolled. */
export function snapshotScrollCandidates(): CandidateSnapshot[] {
  const snapshots: CandidateSnapshot[] = [];
  const seen = new Set<Element>();

  // Named candidates
  for (const sel of CANDIDATE_SELECTORS) {
    document.querySelectorAll(sel).forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      const html = el as HTMLElement;
      const cs = getComputedStyle(html);
      snapshots.push({
        selector: sel,
        scrollTop: html.scrollTop,
        clientHeight: html.clientHeight,
        scrollHeight: html.scrollHeight,
        overflowY: cs.overflowY,
        isScrollable: html.scrollHeight > html.clientHeight && /auto|scroll/.test(cs.overflowY),
      });
    });
  }

  // Document-level
  const docEl = document.documentElement;
  if (!seen.has(docEl)) {
    seen.add(docEl);
    snapshots.push({
      selector: 'document.documentElement',
      scrollTop: docEl.scrollTop,
      clientHeight: docEl.clientHeight,
      scrollHeight: docEl.scrollHeight,
      overflowY: getComputedStyle(docEl).overflowY,
      isScrollable: docEl.scrollHeight > docEl.clientHeight,
    });
  }

  // Find any other scrolled element (scrollTop > 0 with overflow auto/scroll)
  document.querySelectorAll('*').forEach((el) => {
    if (seen.has(el)) return;
    const html = el as HTMLElement;
    if (html.scrollTop > 0) {
      const cs = getComputedStyle(html);
      if (/auto|scroll/.test(cs.overflowY) && html.scrollHeight > html.clientHeight) {
        snapshots.push({
          selector: describeElement(html),
          scrollTop: html.scrollTop,
          clientHeight: html.clientHeight,
          scrollHeight: html.scrollHeight,
          overflowY: cs.overflowY,
          isScrollable: true,
        });
      }
    }
  });

  // Window
  snapshots.push({
    selector: 'window',
    scrollTop: window.scrollY,
    clientHeight: window.innerHeight,
    scrollHeight: document.documentElement.scrollHeight,
    overflowY: 'n/a',
    isScrollable: window.scrollY > 0 || document.documentElement.scrollHeight > window.innerHeight,
  });

  return snapshots;
}

function describeElement(el: HTMLElement): string {
  let desc = el.tagName.toLowerCase();
  if (el.id) desc += `#${el.id}`;
  if (el.className && typeof el.className === 'string') {
    desc += '.' + el.className.split(/\s+/).slice(0, 3).join('.');
  }
  return desc;
}

/** Log a labeled snapshot to console. */
export function logScrollSnapshot(label: string): void {
  if (!isScrollDebugEnabled()) return;
  const snap = snapshotScrollCandidates();
  const scrolled = snap.filter(s => s.scrollTop > 0);
  console.log(
    `[ScrollDiag] ${label}`,
    scrolled.length > 0
      ? { scrolled, all: snap }
      : { message: 'all at 0', count: snap.length }
  );
}

// ─── Authoritative scroller resolver ────────────────────────────────────

/**
 * Returns the single authoritative scroll container for the app.
 * On Capacitor iOS this is always `.app-root`.
 * On web this is `document.documentElement` (or `window` equivalent).
 */
export function getAuthoritativeScroller(): HTMLElement {
  if (isCapacitorIOS()) {
    return appRoot() || document.documentElement;
  }
  return (document.scrollingElement as HTMLElement) || document.documentElement;
}

/**
 * Force-reset scroll on the authoritative container + window.
 * Uses instant behavior and disables smooth scrolling temporarily.
 */
export function forceResetScroll(): void {
  const scroller = getAuthoritativeScroller();

  // Temporarily disable smooth scroll
  const prevBehavior = scroller.style.scrollBehavior;
  scroller.style.scrollBehavior = 'auto';

  scroller.scrollTop = 0;
  scroller.scrollLeft = 0;
  scroller.scrollTo({ top: 0, left: 0, behavior: 'auto' });

  // Always also reset window (belt and suspenders)
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

  // Reset any Radix scroll areas
  document.querySelectorAll('[data-radix-scroll-area-viewport]').forEach((el) => {
    (el as HTMLElement).scrollTop = 0;
  });

  requestAnimationFrame(() => {
    scroller.style.scrollBehavior = prevBehavior;
  });
}
